/* ArmorBatch Global — Vault Entrance.
   The mandatory front door for the public: a sealed silver vault, one handle to spin, and
   the swing hands the visitor to the public Showcase. The real login lives behind a private
   link (see lib/accessKey.js) and is never advertised here. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, PhoneCall, LogIn } from "lucide-react";
import { ACCESS_WORD } from "@/lib/accessKey";

const DOOR_IMG = "https://static.prod-images.emergentagent.com/jobs/448b2756-e759-4bdc-a399-e866d3ac841b/images/35033f8a33a94c7c6593b92ad0aea1ab65624e0e274eebbfb17f45c6137b87c8.jpeg";
const SWING_MS = 3000;
const CONTACT = "admin@armorbatch.com";


/* Hydraulic release + metallic clink, synthesised so there's no audio asset to ship or
   fail to load. Only ever fires after the visitor's click, so autoplay policy is happy. */
function useVaultSound() {
  const ctxRef = useRef(null);
  const ctx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    return ctxRef.current;
  };
  const noise = (c, t, dur, from, to, gain) => {
    const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(); src.buffer = buf;
    const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 0.9;
    bp.frequency.setValueAtTime(from, t); bp.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp); bp.connect(g); g.connect(c.destination);
    src.start(t); src.stop(t + dur);
  };
  const ring = (c, t, freq, dur, gain, type = "triangle") => {
    const o = c.createOscillator(); o.type = type; o.frequency.setValueAtTime(freq, t);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(c.destination);
    o.start(t); o.stop(t + dur);
  };
  /** Bolt release + long hydraulic hiss under the swing. */
  const release = useCallback(() => {
    const c = ctx(); if (!c) return;
    if (c.state === "suspended") c.resume();
    const t = c.currentTime;
    ring(c, t, 88, 0.5, 0.35, "square");            // bolt thunk
    ring(c, t + 0.02, 176, 0.3, 0.12);
    noise(c, t + 0.1, 2.5, 1200, 240, 0.09);        // hydraulic breath
    noise(c, t + 0.35, 2.3, 180, 70, 0.07);         // low steel groan
  }, []);
  /** Metallic clink as the door finishes its swing. */
  const clink = useCallback(() => {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;
    [2450, 3180, 4270].forEach((f, i) => ring(c, t + i * 0.012, f, 0.9 - i * 0.2, 0.12 - i * 0.03));
    ring(c, t, 140, 0.6, 0.2, "square");
    noise(c, t, 0.35, 5200, 1400, 0.05);
  }, []);
  return { release, clink };
}

/* "Leave your number" — a lead lands in the staff Portal Inbox and emails admin, so an
   interested plant owner at 3am their time is a callback waiting, not a lost tab. */
export function LeadForm({ dark = false }) {
  const [f, setF] = useState({ name: "", phone: "", company: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || f.phone.trim().length < 5) {
      toast.error("Please add your name and a phone number we can reach you on");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/leads", { ...f, source: "vault" });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send that — please email us instead");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className={`mt-8 border px-5 py-4 flex items-center gap-3 ${dark ? "border-emerald-700 bg-emerald-950/40" : "border-emerald-300 bg-emerald-50"}`} data-testid="vault-lead-sent">
        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
        <div>
          <div className={`font-bold ${dark ? "text-emerald-300" : "text-zinc-900"}`}>Got it — we'll call you.</div>
          <div className={`text-sm ${dark ? "text-zinc-400" : "text-zinc-600"}`}>Your details are with us. If it's urgent, email {CONTACT}.</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`mt-8 pt-6 ${dark ? "" : "border-t border-zinc-200"}`} data-testid="vault-lead-form">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Or leave your number and we'll call you</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Input value={f.name} onChange={set("name")} placeholder="Your name *" data-testid="vault-lead-name"
          className="rounded-none border-zinc-300 h-11 text-base text-zinc-900 placeholder:text-zinc-400" />
        <Input value={f.phone} onChange={set("phone")} placeholder="Phone (with country code) *" data-testid="vault-lead-phone"
          className="rounded-none border-zinc-300 h-11 text-base text-zinc-900 placeholder:text-zinc-400" />
        <Input value={f.company} onChange={set("company")} placeholder="Company / plant" data-testid="vault-lead-company"
          className="rounded-none border-zinc-300 h-11 text-base text-zinc-900 placeholder:text-zinc-400" />
        <Input value={f.country} onChange={set("country")} placeholder="Country" data-testid="vault-lead-country"
          className="rounded-none border-zinc-300 h-11 text-base text-zinc-900 placeholder:text-zinc-400" />
      </div>
      <button type="submit" disabled={busy} data-testid="vault-lead-submit"
        className={`mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 ${dark ? "bg-[#F57C00] text-black hover:bg-[#ff8f1f]" : "bg-zinc-900 hover:bg-[#1D4ED8]"} disabled:opacity-50 text-white text-sm font-bold uppercase tracking-widest px-7 py-3.5 transition-colors duration-300`}>
        <PhoneCall className="w-4 h-4" /> {busy ? "Sending…" : "Call me back"}
      </button>
    </form>
  );
}

export default function VaultEntrance() {
  const [phase, setPhase] = useState("closed");   // closed → opening → (showcase)
  const { release, clink } = useVaultSound();
  const nav = useNavigate();
  const timers = useRef([]);
  const at = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Turning the handle opens the door onto the public flyer — the brochure, not the console.
  const open = () => {
    if (phase !== "closed") return;
    release();
    setPhase("opening");
    at(() => { clink(); nav("/showcase"); }, SWING_MS);
  };

  const open_ = phase !== "closed";

  return (
    <div className="fixed inset-0 bg-black overflow-hidden vault-root" data-testid="vault-entrance" data-phase={phase}>
      <a href={`/login?key=${ACCESS_WORD}`} data-testid="vault-login-link"
        className="absolute top-4 right-4 z-50 inline-flex items-center gap-2 px-4 py-2 text-white text-[11px] font-bold uppercase tracking-widest border border-white/30 bg-black/50 hover:bg-[#0052CC] hover:border-[#0052CC] transition-colors">
        <LogIn className="w-3.5 h-3.5" /> Log in
      </a>
      <style>{`
        .vault-root { --swing: ${SWING_MS}ms; }
        @keyframes vaultSwing {
          0%   { transform: rotateY(0deg)   translateZ(0); }
          12%  { transform: rotateY(-2.2deg) translateZ(0); }
          100% { transform: rotateY(-114deg) translateZ(0); }
        }
        @keyframes handleSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(825deg); } }
        @keyframes handleHint { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-9deg); } }
        @keyframes vaultLightUp { 0% { opacity: 0; } 100% { opacity: 1; } }
        .vault-stage { perspective: 1700px; perspective-origin: 62% 50%; }
        .vault-door {
          transform-origin: left center;
          transform-style: preserve-3d;
          backface-visibility: hidden;
          will-change: transform;
        }
        .vault-door.is-open {
          animation: vaultSwing var(--swing) cubic-bezier(.72,.02,.28,1) forwards;
        }
        .vault-handle-spin { animation: handleSpin var(--swing) cubic-bezier(.62,.04,.3,1) forwards; }
        .vault-handle-hint { animation: handleHint 2.6s ease-in-out infinite; }
        .vault-fade-in { animation: vaultLightUp 900ms ease forwards; }
        @media (prefers-reduced-motion: reduce) {
          .vault-door.is-open { animation-duration: 300ms; }
          .vault-handle-hint, .vault-handle-spin { animation: none; }
        }
      `}</style>

      {/* ── the vault door ─────────────────────────────────────── */}
      {(phase === "closed" || phase === "opening") && (
        <div className={`absolute inset-0 vault-stage ${open_ ? "pointer-events-none" : ""}`}>
          <div className={`absolute inset-0 vault-door ${open_ ? "is-open" : ""}`}>
            <img src={DOOR_IMG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            {/* light raking across the steel + edge shadow so it reads as a thick slab */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(105deg, rgba(255,255,255,.16) 0%, rgba(255,255,255,0) 34%, rgba(0,0,0,.42) 78%, rgba(0,0,0,.66) 100%)" }} />
            <div className="absolute inset-y-0 left-0 w-[3vw] pointer-events-none"
              style={{ background: "linear-gradient(90deg, rgba(0,0,0,.75), rgba(0,0,0,0))" }} />

            {/* wordmark */}
            <div className="absolute top-[7vh] left-0 right-0 text-center px-6">
              <div className="mono text-[10px] sm:text-xs tracking-[0.55em] uppercase text-zinc-300/80">ArmorBatch</div>
              <div className="mono text-[9px] sm:text-[10px] tracking-[0.42em] uppercase text-[#F57C00]/80 mt-1.5">Global</div>
            </div>

            {/* handle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                onClick={open}
                aria-label="Open the vault"
                data-testid="vault-handle"
                className="group relative w-[34vmin] h-[34vmin] rounded-full outline-none pointer-events-auto cursor-pointer"
              >
                <div className="absolute inset-[-14%] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "radial-gradient(circle, rgba(245,124,0,.30), rgba(245,124,0,0) 66%)" }} />
                <div className={`absolute inset-0 ${open_ ? "vault-handle-spin" : "vault-handle-hint"} transition-transform duration-500 group-hover:scale-[1.04]`}>
                  <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-[0_18px_34px_rgba(0,0,0,.75)]">
                    <defs>
                      <linearGradient id="steel" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#FAFAFA" /><stop offset="22%" stopColor="#B9BDC4" />
                        <stop offset="48%" stopColor="#71767E" /><stop offset="70%" stopColor="#D8DBE0" />
                        <stop offset="100%" stopColor="#5C6268" />
                      </linearGradient>
                      <linearGradient id="steel2" x1="0" y1="1" x2="1" y2="0">
                        <stop offset="0%" stopColor="#8A9099" /><stop offset="40%" stopColor="#EDEFF2" />
                        <stop offset="60%" stopColor="#9AA0A8" /><stop offset="100%" stopColor="#61666D" />
                      </linearGradient>
                      <radialGradient id="hub" cx="38%" cy="32%">
                        <stop offset="0%" stopColor="#F4F5F7" /><stop offset="55%" stopColor="#9BA1A9" />
                        <stop offset="100%" stopColor="#4A4F56" />
                      </radialGradient>
                    </defs>
                    <circle cx="100" cy="100" r="92" fill="none" stroke="#2A2D31" strokeWidth="14" />
                    <circle cx="100" cy="100" r="92" fill="none" stroke="url(#steel)" strokeWidth="9" />
                    <circle cx="100" cy="100" r="80" fill="none" stroke="url(#steel2)" strokeWidth="4" strokeOpacity="0.7" />
                    {[0, 72, 144, 216, 288].map((a) => (
                      <g key={a} transform={`rotate(${a} 100 100)`}>
                        <rect x="94" y="22" width="12" height="62" rx="4" fill="url(#steel)" stroke="#33373C" strokeWidth="1.2" />
                      </g>
                    ))}
                    <circle cx="100" cy="100" r="30" fill="url(#hub)" stroke="#33373C" strokeWidth="2" />
                    <circle cx="100" cy="100" r="17" fill="none" stroke="#4C5157" strokeWidth="3" />
                    <circle cx="100" cy="100" r="7" fill="#2F3338" />
                    {Array.from({ length: 36 }).map((_, i) => (
                      <rect key={i} x="99.2" y="70" width="1.6" height="6" fill="#4A4F56" transform={`rotate(${i * 10} 100 100)`} />
                    ))}
                  </svg>
                </div>
              </button>
            </div>

            {/* prompt */}
            <div className={`absolute left-0 right-0 bottom-[9vh] text-center px-6 transition-opacity duration-500 ${open_ ? "opacity-0" : "opacity-100"}`}>
              <div className="mono text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-zinc-200/90">Turn the handle</div>
              <div className="mono text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-zinc-400/70 mt-2">
                See what runs inside
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
